import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Web as WebIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Code as CustomIcon
} from '@mui/icons-material';

/**
 * ProjectRequirementsStep Component - User Story 2
 * Second step in the onboarding wizard for collecting project requirements
 * 
 * Following VegaDigital Constitution: WCAG 2.1 AA compliance, comprehensive validation
 */

interface ProjectRequirementsData {
  project_type?: string;
  project_description?: string;
  features?: string[];
  platforms?: string[];
  expected_users?: string;
  performance_priority?: string;
  integration_needs?: string[];
  compliance_requirements?: string[];
}

interface ProjectRequirementsStepProps {
  data: ProjectRequirementsData;
  onChange: (data: ProjectRequirementsData) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string[]>;
}

const PROJECT_TYPES = [
  {
    value: 'web-application',
    label: 'Web Application',
    description: 'Custom web-based software solutions',
    icon: <WebIcon />,
    features: ['user-authentication', 'admin-dashboard', 'data-management', 'reporting']
  },
  {
    value: 'mobile-app',
    label: 'Mobile Application',
    description: 'iOS and/or Android mobile applications',
    icon: <MobileIcon />,
    features: ['push-notifications', 'offline-support', 'camera-integration', 'location-services']
  },
  {
    value: 'e-commerce',
    label: 'E-commerce Platform',
    description: 'Online store and shopping solutions',
    icon: <EcommerceIcon />,
    features: ['payment-processing', 'inventory-management', 'order-management', 'customer-accounts']
  },
  {
    value: 'custom-software',
    label: 'Custom Software',
    description: 'Specialized business software solutions',
    icon: <CustomIcon />,
    features: ['workflow-automation', 'data-integration', 'custom-reporting', 'api-development']
  }
];

const COMMON_FEATURES = [
  { value: 'user-authentication', label: 'User Authentication & Authorization' },
  { value: 'admin-dashboard', label: 'Admin Dashboard' },
  { value: 'payment-processing', label: 'Payment Processing' },
  { value: 'inventory-management', label: 'Inventory Management' },
  { value: 'order-management', label: 'Order Management' },
  { value: 'customer-accounts', label: 'Customer Account Management' },
  { value: 'data-management', label: 'Data Management' },
  { value: 'reporting', label: 'Analytics & Reporting' },
  { value: 'push-notifications', label: 'Push Notifications' },
  { value: 'offline-support', label: 'Offline Support' },
  { value: 'camera-integration', label: 'Camera Integration' },
  { value: 'location-services', label: 'Location Services' },
  { value: 'workflow-automation', label: 'Workflow Automation' },
  { value: 'data-integration', label: 'Data Integration' },
  { value: 'custom-reporting', label: 'Custom Reporting' },
  { value: 'api-development', label: 'API Development' },
  { value: 'real-time-updates', label: 'Real-time Updates' },
  { value: 'file-upload', label: 'File Upload & Management' },
  { value: 'search-functionality', label: 'Advanced Search' },
  { value: 'multi-language', label: 'Multi-language Support' }
];

const PLATFORMS = [
  { value: 'web', label: 'Web Browser' },
  { value: 'mobile-responsive', label: 'Mobile-Responsive Web' },
  { value: 'ios', label: 'iOS App' },
  { value: 'android', label: 'Android App' },
  { value: 'desktop', label: 'Desktop Application' },
  { value: 'tablet', label: 'Tablet Optimized' }
];

const PERFORMANCE_PRIORITIES = [
  { value: 'speed', label: 'Speed & Performance', description: 'Fast loading and response times' },
  { value: 'scalability', label: 'Scalability', description: 'Ability to handle growth' },
  { value: 'reliability', label: 'Reliability', description: 'High uptime and stability' },
  { value: 'security', label: 'Security', description: 'Data protection and compliance' },
  { value: 'usability', label: 'User Experience', description: 'Intuitive and easy to use' }
];

const INTEGRATION_OPTIONS = [
  { value: 'crm', label: 'CRM Systems (Salesforce, HubSpot)' },
  { value: 'erp', label: 'ERP Systems (SAP, Oracle)' },
  { value: 'payment', label: 'Payment Gateways (Stripe, PayPal)' },
  { value: 'analytics', label: 'Analytics (Google Analytics, Mixpanel)' },
  { value: 'email-marketing', label: 'Email Marketing (Mailchimp, SendGrid)' },
  { value: 'social-media', label: 'Social Media APIs' },
  { value: 'accounting', label: 'Accounting Software (QuickBooks, Xero)' },
  { value: 'shipping', label: 'Shipping Providers (FedEx, UPS)' },
  { value: 'inventory', label: 'Inventory Management Systems' },
  { value: 'other', label: 'Other (specify in description)' }
];

export const ProjectRequirementsStep: React.FC<ProjectRequirementsStepProps> = ({
  data,
  onChange,
  onValidationChange,
  errors = {}
}) => {
  const [localData, setLocalData] = useState<ProjectRequirementsData>(data);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [showConditionalFields, setShowConditionalFields] = useState(false);

  // Initialize data from props
  useEffect(() => {
    if (Object.keys(data).length > 0 && Object.keys(localData).length === 0) {
      setLocalData(data);
    }
  }, [data]);

  // Validate form and update parent
  useEffect(() => {
    const newErrors: Record<string, string> = {};

    if (!localData.project_type) {
      newErrors.project_type = 'Please select a project type';
    }

    if (!localData.project_description?.trim()) {
      newErrors.project_description = 'Project description is required';
    } else if (localData.project_description.trim().length < 10) {
      newErrors.project_description = 'Please provide a more detailed description (at least 10 characters)';
    }

    if (!localData.platforms?.length) {
      newErrors.platforms = 'Please select at least one platform';
    }

    const isValid = Object.keys(newErrors).length === 0 && 
                   !!localData.project_type &&
                   !!localData.project_description?.trim() &&
                   !!(localData.platforms?.length);

    // Update errors state
    setLocalErrors(newErrors);
    
    // Update conditional fields visibility
    setShowConditionalFields(!!localData.project_type);
    
    // Notify parent of changes
    onChange(localData);
    onValidationChange(isValid);
  }, [localData, onChange, onValidationChange]);

  const handleProjectTypeChange = useCallback((value: string) => {
    const selectedType = PROJECT_TYPES.find(type => type.value === value);
    setLocalData(prev => ({
      ...prev,
      project_type: value,
      // Pre-select common features for the project type
      features: selectedType?.features || []
    }));
  }, []);

  const handleFeatureChange = useCallback((feature: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      features: checked 
        ? [...(prev.features || []), feature]
        : (prev.features || []).filter(f => f !== feature)
    }));
  }, []);

  const handlePlatformChange = useCallback((platform: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      platforms: checked 
        ? [...(prev.platforms || []), platform]
        : (prev.platforms || []).filter(p => p !== platform)
    }));
  }, []);

  const handleIntegrationChange = useCallback((integration: string, checked: boolean) => {
    setLocalData(prev => ({
      ...prev,
      integration_needs: checked 
        ? [...(prev.integration_needs || []), integration]
        : (prev.integration_needs || []).filter(i => i !== integration)
    }));
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalData(prev => ({ ...prev, project_description: e.target.value }));
  }, []);

  const handleExpectedUsersChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalData(prev => ({ ...prev, expected_users: e.target.value }));
  }, []);

  const handlePerformancePriorityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalData(prev => ({ ...prev, performance_priority: e.target.value }));
  }, []);

  const getError = (field: string): string => {
    return errors[field]?.[0] || localErrors[field] || '';
  };

  const hasError = (field: string): boolean => {
    return !!(errors[field]?.length || localErrors[field]);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Project Requirements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tell us about your project so we can understand your needs and provide the best solution.
      </Typography>

      {/* Project Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          What type of project are you looking to build?
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {PROJECT_TYPES.map((type) => (
            <Box key={type.value} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' } }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: localData.project_type === type.value ? 2 : 1,
                  borderColor: localData.project_type === type.value ? 'primary.main' : 'divider',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => handleProjectTypeChange(type.value)}
                data-testid={`project-type-${type.value}`}
              >
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ color: 'primary.main', mb: 1 }}>
                    {type.icon}
                  </Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {type.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {type.description}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
        {hasError('project_type') && (
          <FormHelperText error sx={{ mt: 1 }}>
            {getError('project_type')}
          </FormHelperText>
        )}
      </Box>

      {/* Project Description */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Project Description"
          value={localData.project_description || ''}
          onChange={handleDescriptionChange}
          error={hasError('project_description')}
          helperText={getError('project_description') || 'Describe your project goals, target audience, and key requirements'}
          multiline
          rows={4}
          required
          inputProps={{
            'aria-label': 'Project Description',
            'data-testid': 'project-description-input'
          }}
        />
      </Box>

      {/* Platforms */}
      <Box sx={{ mb: 4 }}>
        <FormControl component="fieldset" error={hasError('platforms')}>
          <FormLabel component="legend">
            <Typography variant="subtitle1">Target Platforms *</Typography>
          </FormLabel>
          <FormGroup row sx={{ mt: 1 }}>
            {PLATFORMS.map((platform) => (
              <FormControlLabel
                key={platform.value}
                control={
                  <Checkbox
                    checked={localData.platforms?.includes(platform.value) || false}
                    onChange={(e) => handlePlatformChange(platform.value, e.target.checked)}
                    data-testid={`platform-${platform.value}`}
                  />
                }
                label={platform.label}
              />
            ))}
          </FormGroup>
          {hasError('platforms') && (
            <FormHelperText>{getError('platforms')}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Conditional Fields */}
      {showConditionalFields && (
        <>
          {/* Key Features */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Key Features & Functionality</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {COMMON_FEATURES.map((feature) => (
                    <Box key={feature.value} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 4px)', md: '1 1 calc(33.333% - 6px)' } }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localData.features?.includes(feature.value) || false}
                            onChange={(e) => handleFeatureChange(feature.value, e.target.checked)}
                            data-testid={`feature-${feature.value}`}
                          />
                        }
                        label={feature.label}
                        sx={{ fontSize: '0.875rem' }}
                      />
                    </Box>
                  ))}
                </Box>
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Performance Priority */}
          <Box sx={{ my: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <Typography variant="subtitle1">What's your primary performance priority?</Typography>
              </FormLabel>
              <RadioGroup
                value={localData.performance_priority || ''}
                onChange={handlePerformancePriorityChange}
                sx={{ mt: 1 }}
              >
                {PERFORMANCE_PRIORITIES.map((priority) => (
                  <FormControlLabel
                    key={priority.value}
                    value={priority.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">{priority.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {priority.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Integration Needs */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Third-party Integrations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select any third-party services you need to integrate with:
              </Typography>
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {INTEGRATION_OPTIONS.map((integration) => (
                    <Box key={integration.value} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 4px)' } }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={localData.integration_needs?.includes(integration.value) || false}
                            onChange={(e) => handleIntegrationChange(integration.value, e.target.checked)}
                            data-testid={`integration-${integration.value}`}
                          />
                        }
                        label={integration.label}
                        sx={{ fontSize: '0.875rem' }}
                      />
                    </Box>
                  ))}
                </Box>
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Expected Users */}
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Expected Number of Users"
              value={localData.expected_users || ''}
              onChange={handleExpectedUsersChange}
              helperText="Estimate the number of users you expect (e.g., 100, 1000, 10,000+)"
              inputProps={{
                'aria-label': 'Expected Users',
                'data-testid': 'expected-users-input'
              }}
            />
          </Box>
        </>
      )}

      {/* Selected Features Summary */}
      {localData.features?.length ? (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Selected Features:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {localData.features.map((feature) => {
              const featureLabel = COMMON_FEATURES.find(f => f.value === feature)?.label || feature;
              return (
                <Chip
                  key={feature}
                  label={featureLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};